/** @typedef {{ value: string, label: string }} CreditActionOption */

export const CREDIT_ACTION = {
  APPLY_TO_INVOICE: "apply_to_invoice",
  REFUND_TO_CARD: "refund_to_card",
  CREDIT_CUSTOMER_ACCOUNT: "credit_customer_account",
};

/** @type {readonly string[]} */
export const REFUNDABLE_INVOICE_STATUSES = ["open", "paid", "uncollectible"];

/** @type {CreditActionOption[]} */
export const CREDIT_ACTION_OPTIONS = [
  {
    value: CREDIT_ACTION.APPLY_TO_INVOICE,
    label: "Apply to invoice",
  },
  {
    value: CREDIT_ACTION.REFUND_TO_CARD,
    label: "Refund to card",
  },
  {
    value: CREDIT_ACTION.CREDIT_CUSTOMER_ACCOUNT,
    label: "Credit to customer's account",
  },
];

/**
 * Normalizes invoice status for consistent comparisons.
 *
 * @param {string} [status] - Raw invoice status from the API.
 * @returns {string} Lowercase status, or empty string when missing.
 */
export function normalizeInvoiceStatus(status) {
  return typeof status === "string" ? status.toLowerCase() : "";
}

/**
 * Whether an invoice row is eligible for the Refund action.
 *
 * @param {{ invoice_id?: string, status?: string }} invoice - Invoice row data.
 * @returns {boolean} True when refund should be shown.
 */
export function isInvoiceRefundEligible(invoice) {
  const status = normalizeInvoiceStatus(invoice?.status);

  return (
    Boolean(invoice?.invoice_id?.includes("in_")) &&
    REFUNDABLE_INVOICE_STATUSES.includes(status)
  );
}

/**
 * Returns credit_action dropdown options for a given invoice status.
 *
 * @param {string} [invoiceStatus] - Invoice status from the invoice row.
 * @returns {CreditActionOption[]} Options available for the status.
 */
export function getCreditActionOptionsForInvoiceStatus(invoiceStatus) {
  const status = normalizeInvoiceStatus(invoiceStatus);

  if (status === "paid") {
    return CREDIT_ACTION_OPTIONS.filter(
      (option) =>
        option.value === CREDIT_ACTION.REFUND_TO_CARD ||
        option.value === CREDIT_ACTION.CREDIT_CUSTOMER_ACCOUNT
    );
  }

  if (REFUNDABLE_INVOICE_STATUSES.includes(status)) {
    return CREDIT_ACTION_OPTIONS.filter(
      (option) => option.value === CREDIT_ACTION.APPLY_TO_INVOICE
    );
  }

  return [];
}

/**
 * Returns allowed credit_action enum values for validation.
 *
 * @param {string} [invoiceStatus] - Invoice status from the invoice row.
 * @returns {string[]} Allowed credit_action values for the status.
 */
export function getAllowedCreditActionValues(invoiceStatus) {
  return getCreditActionOptionsForInvoiceStatus(invoiceStatus).map(
    (option) => option.value
  );
}

/**
 * Whether the credit_action field applies for a given invoice status.
 *
 * @param {string} [invoiceStatus] - Invoice status from the invoice row.
 * @returns {boolean} True when credit_action options should be shown and validated.
 */
export function isCreditActionApplicable(invoiceStatus) {
  return getCreditActionOptionsForInvoiceStatus(invoiceStatus).length > 0;
}
